import { AutoScrollActivator, DndContext, DragEndEvent, MouseSensor, TouchSensor, TraversalOrder, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import CustomerEditor from 'features/appointment/pages/AppointmentsPage/components/Calendar/widgets/CustomerEditor';
import appointmentActions from 'features/appointment/services/actions';
import { IBodyApiUpdateAppointmentWithDrop } from 'features/appointment/services/types/api';
import { ICalendarDndData, ICalendarItemData, ICalendarSchema } from 'features/appointment/services/types/calendar';
import { set } from 'lodash';
import moment from 'moment';
import { memo } from 'react';
import { useAppDispatch } from 'store/hooks';
import { momentTimezone } from 'utils/time';
import useCalendarMapping from '../hooks/useCalendarMapping';
import CalendarStyled from '../styles';
import DraggableOverlay from './DnD/DraggableOverlay';
import RealtimeLine from './RealtimeLine';
import CalendarViewRow from './Row';
const DATE_VALUE_FORMAT = 'MM-DD-YYYY HH:mm:ss';
const dateRex = /(^0[1-9]|[12][0-9]|3[01])-(0[1-9]|1[0-2])-(\d{4}$)/;
const distanceTimeline = 15;
type IProps = {
  modal?: boolean;
  schema: ICalendarSchema;
  onDragStart: () => void;
  onDragEnd: () => void;
};
const autoscrollSetting = {
  // Dùng vị trí con trỏ (mặc định). Có thể thử DraggableRect nếu hợp layout bạn
  activator: AutoScrollActivator.Pointer,
  // Vùng biên theo trục X nhỏ lại => chạm mép trái sẽ kích hoạt sớm hơn
  threshold: { x: 0.3, y: 0.18 }, // 6% ngang, 18% dọc (tuỳ màn hình mà chỉnh)
  // Tăng tốc cuộn: lớn hơn mặc định (~10) sẽ nhanh “tăng tốc”
  acceleration: 100,
  // Tick ngắn hơn (ms) => cập nhật cuộn dày hơn, cảm giác mượt/nhanh hơn
  interval: 2,
  // Ưu tiên thứ tự tổ tiên scroll (tuỳ layout; thử TreeOrder/ReversedTreeOrder)
  order: TraversalOrder.TreeOrder,
  // Chỉ cho phép container lịch của bạn được cuộn (tránh window cuộn không mong muốn)
  canScroll: (el: any) => el?.id === 'drag-scroll-container',
};

const CalendarViewBody = ({ schema, modal, onDragStart, onDragEnd: onDragEndProp }: IProps) => {
  const body = useCalendarMapping(schema);
  const dispatch = useAppDispatch();
  const mouseSensor = useSensor(MouseSensor, {
    // Require the mouse to move by 10 pixels before activating
    activationConstraint: {
      distance: 10,
    },
  });
  const touchSensor = useSensor(TouchSensor, {
    // Press delay of 250ms, with tolerance of 5px of movement
    activationConstraint: {
      delay: 500,
      tolerance: 8,
    },
  });

  const sensors = useSensors(mouseSensor, touchSensor,);

  const onDragEnd = (event: DragEndEvent) => {
    onDragEndProp?.();
    const appointmentItem = (event.active?.data?.current?.data) as ICalendarItemData;
    if (!appointmentItem) return;
    const [timeStr, droppable] = String(event.over?.id || '').split('/');
    const overData = event.over?.data.current as ICalendarDndData;

    if (overData?.blockDrop) return;

    let timeStart = momentTimezone(appointmentItem.startTime);
    const timeEnd = momentTimezone(appointmentItem.endTime);
    const distance = timeEnd.diff(timeStart?.clone(), 'minutes');

    if (dateRex.test(droppable)) {
      timeStart = moment(droppable, 'DD-MM-YYYY');
    }

    const time = moment(timeStr, 'HH:mm');
    timeStart.set({
      hour: time.get('hour'),
      minute: time.get('minute'),
      second: 0,
    });
    const payload: IBodyApiUpdateAppointmentWithDrop = {
      appointmentId: appointmentItem.id,
      startTime: timeStart.format(DATE_VALUE_FORMAT),
      distance,
    };

    if (!dateRex.test(droppable)) {
      set(payload, 'staffId', droppable);
    }

    dispatch(appointmentActions.updateAppointmentWithDrop(payload));
  };

  if (modal) {
    return (
      <CalendarStyled.TBody>
        {body.rows.map((row, index) => (
          <CalendarViewRow
            key={row.id}
            data={row}
            index={index}
            rowsSpanInfo={body.rowsSpanInfo}
            distance={distanceTimeline}
            v2
          />
        ))}
        <RealtimeLine />
      </CalendarStyled.TBody>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      autoScroll={autoscrollSetting}
    >
      <CalendarStyled.TBody>
        {body.rows.map((row, index) => (
          <CalendarViewRow
            key={row.id}
            data={row}
            index={index}
            rowsSpanInfo={body.rowsSpanInfo}
            distance={distanceTimeline}
            v2
          />
        ))}
        <RealtimeLine />
      </CalendarStyled.TBody>
      <DraggableOverlay />
      <CustomerEditor />
    </DndContext>
  );
};

export default memo(CalendarViewBody);
